/**
 * PaywallProductsListView Component
 *
 * List view for paywall products with table display and actions.
 * Follows OnboardingsListView pattern.
 *
 * @module paywall-products/components/PaywallProductsListView
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import RefreshIcon from '@mui/icons-material/Refresh';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { GUIDE_ANCHORS } from '@modules/guides/anchors/guideAnchorRegistry';
import { PAYWALL_PRODUCTS_TEST_IDS } from '@modules/paywall-products/testIds';
import type { PaywallProductsMode } from '@modules/paywall-products/utils/modeParam';
import type { PaywallProduct } from '@segmently/schema-models/payments';

/**
 * Props for PaywallProductsListView
 */
export interface PaywallProductsListViewProps {
  /** List of paywall products to display */
  products: PaywallProduct[];

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Whether Stripe is connected */
  isStripeConnected: boolean;

  /** Whether Paddle is connected */
  isPaddleConnected?: boolean;

  /** Callback when create button is clicked (receives current tab mode) */
  onCreateProduct: (event: React.MouseEvent<HTMLElement>, mode: 'test' | 'live') => void;

  /** Callback when edit is clicked */
  onEditProduct: (product: PaywallProduct) => void;

  /** Callback when delete is clicked */
  onDeleteProduct: (product: PaywallProduct) => void;

  /** Callback to refresh the list */
  onRefresh: () => void;

  /** Initial Test/Live tab (from `?mode=`); the component owns the state after mount. */
  initialMode?: PaywallProductsMode;
}

/**
 * PaywallProductsListView Component
 */
export function PaywallProductsListView({
  products,
  loading,
  error,
  isStripeConnected,
  isPaddleConnected = false,
  onCreateProduct,
  onEditProduct,
  onDeleteProduct,
  onRefresh,
  initialMode,
}: PaywallProductsListViewProps) {
  const theme = useTheme();
  const guideService = React.useMemo(() => new GuideService(), []);

  // At least one provider connected
  const anyProviderConnected = isStripeConnected || isPaddleConnected;

  // Mode filter state (test/sandbox or live/production)
  const [selectedMode, setSelectedMode] = useState<'test' | 'live'>(initialMode ?? 'test');

  /**
   * Get the effective mode/environment for a product
   * Stripe uses 'mode' (test/live), Paddle uses 'paddleEnvironment' (sandbox/production)
   */
  const getProductMode = (product: PaywallProduct): 'test' | 'live' => {
    if (product.paymentProvider === 'paddle') {
      // Paddle: sandbox → test, production → live
      return product.paddleEnvironment === 'production' ? 'live' : 'test';
    }
    // Stripe: uses mode directly
    return product.mode || 'test';
  };

  // Filter products by selected mode
  const filteredProducts = products.filter(
    (p) => getProductMode(p) === selectedMode
  );

  // Count products by mode
  const testCount = products.filter((p) => getProductMode(p) === 'test').length;
  const liveCount = products.filter((p) => getProductMode(p) === 'live').length;

  // Menu state for row actions
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedProduct, setSelectedProduct] = useState<PaywallProduct | null>(null);

  const handleMenuOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>, product: PaywallProduct) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
      setSelectedProduct(product);
    },
    []
  );

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedProduct(null);
  }, []);

  const handleEdit = useCallback(() => {
    if (selectedProduct) {
      onEditProduct(selectedProduct);
    }
    handleMenuClose();
  }, [selectedProduct, onEditProduct, handleMenuClose]);

  const handleDelete = useCallback(() => {
    if (selectedProduct) {
      onDeleteProduct(selectedProduct);
    }
    handleMenuClose();
  }, [selectedProduct, onDeleteProduct, handleMenuClose]);

  /**
   * Format interval for display
   */
  const formatInterval = (product: PaywallProduct): string => {
    if (product.type === 'one_time') {
      return 'One-time';
    }
    if (!product.subscription) {
      return 'Subscription';
    }
    const { interval, intervalCount } = product.subscription;
    if (intervalCount === 1) {
      return `per ${interval}`;
    }
    return `every ${intervalCount} ${interval}s`;
  };

  /**
   * Format price for display
   */
  const formatPrice = (product: PaywallProduct): string => {
    if (product.preBakedPrice?.localized?.['en']?.displayPrice) {
      return product.preBakedPrice.localized['en'].displayPrice;
    }
    if (product.preBakedPrice?.raw) {
      const { unitAmount, currency } = product.preBakedPrice.raw;
      if (unitAmount !== undefined && currency) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency.toUpperCase(),
        }).format(unitAmount / 100);
      }
    }
    return '—';
  };

  // No provider connected state
  if (!anyProviderConnected) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          Connect your Stripe or Paddle account to create paywall products.
        </Alert>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<RefreshIcon />} onClick={onRefresh}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header with Create button */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <WithGuideHelp
          guideKey={GUIDE_ANCHORS.paywallProductsList.guideKey}
          guideService={guideService}
          data-testid={GUIDE_ANCHORS.paywallProductsList.testId}
          position="inline-end"
        >
          <Typography variant="h6">Paywall Products</Typography>
        </WithGuideHelp>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => onCreateProduct(e, selectedMode)}
            disabled={loading}
            data-testid="pp-add-btn"
          >
            Add Product
          </Button>
        </Box>
      </Box>

      {/* Mode tabs (Test / Live) */}
      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <WithGuideHelp
          guideKey={GUIDE_ANCHORS.paywallProductsModeTabs.guideKey}
          guideService={guideService}
          data-testid={GUIDE_ANCHORS.paywallProductsModeTabs.testId}
          position="top-right"
          sx={{ display: 'block' }}
        >
          <Tabs
            value={selectedMode}
            onChange={(_, value) => setSelectedMode(value)}
            sx={{ px: 2 }}
          >
            <Tab
              value="test"
              data-testid="pp-mode-test"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🧪 Test Products
                  {testCount > 0 && (
                    <Chip label={testCount} size="small" color="warning" />
                  )}
                </Box>
              }
            />
            <Tab
              value="live"
              data-testid="pp-mode-live"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  🚀 Live Products
                  {liveCount > 0 && (
                    <Chip label={liveCount} size="small" color="success" />
                  )}
                </Box>
              }
            />
          </Tabs>
        </WithGuideHelp>
      </Box>

      {/* Loading state */}
      {loading && products.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Empty state */}
      {!loading && filteredProducts.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 8,
            px: 3,
          }}
        >
          <PaymentIcon
            sx={{ fontSize: 64, color: theme.palette.action.disabled, mb: 2 }}
          />
          <Typography variant="h6" sx={{ mb: 1 }}>
            {products.length === 0
              ? 'No paywall products yet'
              : `No ${selectedMode} mode products`}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 3, textAlign: 'center', maxWidth: 400 }}
          >
            {products.length === 0
              ? 'Create paywall products to monetize your onboarding funnels. Link products and prices from your connected Stripe or Paddle account.'
              : `Switch to ${selectedMode === 'test' ? 'live' : 'test'} mode to see other products, or create a new ${selectedMode} product.`}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => onCreateProduct(e, selectedMode)}
          >
            {products.length === 0 ? 'Create First Product' : `Add ${selectedMode === 'test' ? 'Test' : 'Live'} Product`}
          </Button>
        </Box>
      )}

      {/* Products table */}
      {filteredProducts.length > 0 && (
        <TableContainer component={Paper} variant="outlined" sx={{ flex: 1 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Provider</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Checkout</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Product/Price IDs</TableCell>
                <TableCell>Trial</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProducts.map((product) => (
                <TableRow
                  key={product.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => onEditProduct(product)}
                  data-testid={`pp-row-${product.id}`}
                  data-product-name={product.name}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {product.type === 'subscription' ? (
                        <SubscriptionsIcon
                          sx={{ color: theme.palette.primary.main, fontSize: 20 }}
                        />
                      ) : (
                        <PaymentIcon
                          sx={{ color: theme.palette.success.main, fontSize: 20 }}
                        />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {product.name}
                        </Typography>
                        {product.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: 'block',
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {product.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {/* Provider Column */}
                  <TableCell>
                    {product.paymentProvider === 'paddle' ? (
                      <Tooltip title={`Paddle ${product.paddleEnvironment}`}>
                        <Chip
                          label="Paddle"
                          size="small"
                          sx={{
                            bgcolor: '#FFDE59',
                            color: '#1B1B1B',
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                    ) : (
                      <Tooltip title={`Stripe ${product.mode || 'test'}`}>
                        <Chip
                          label="Stripe"
                          size="small"
                          sx={{
                            bgcolor: '#635BFF',
                            color: '#fff',
                            fontWeight: 600,
                          }}
                        />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatInterval(product)}
                      size="small"
                      color={product.type === 'subscription' ? 'primary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={product.checkoutMode === 'embedded' ? 'Embedded' : 'Redirect'}
                      size="small"
                      color={product.checkoutMode === 'embedded' ? 'secondary' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatPrice(product)}</Typography>
                  </TableCell>
                  {/* Product/Price IDs Column */}
                  <TableCell>
                    <Box>
                      {product.paymentProvider === 'paddle' ? (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', display: 'block' }}
                          >
                            {product.paddleProductId?.slice(0, 20)}...
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {product.paddlePriceId?.slice(0, 20)}...
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace', display: 'block' }}
                          >
                            {product.stripeProductId?.slice(0, 20)}...
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {product.stripePriceId?.slice(0, 20)}...
                          </Typography>
                        </>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {product.subscription?.trialDays ? (
                      <Chip
                        label={`${product.subscription.trialDays} days`}
                        size="small"
                        color="info"
                        variant="outlined"
                      />
                    ) : product.subscription?.paidTrial?.enabled ? (
                      <Chip
                        label="Paid trial"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, product)}
                      data-testid={PAYWALL_PRODUCTS_TEST_IDS.rowMenuBtn(product.id)}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Actions menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: theme.palette.error.main }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default PaywallProductsListView;
